import { Grid, Paper } from "@mui/material";
import { action, API, controller } from "api";
import { Button, FormErrorSummary, Input, InputMultFile } from "controls";
import { Model, ToFormData, UseForm, useValidationSummary } from "hooks";
import { Form } from "layout";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import InputFyDate from "./InputFyDate";
import dayjs, { toStoreTime } from "../../../../utils/dayjs";

const AddEditFiscalYear = ({
  setShowForm,
  updateData,
  setReloadKey,
  reloadSystem = false,
}) => {
  const { t } = useTranslation(['common', 'forms', 'tables', 'alerts']);
  const { FiscalYearMdl } = Model();
  const {
    values,
    setValues,
    errors,
    setErrors,
    handleInpChg,
    handleInpDate,
    handleFile,
  } = UseForm(FiscalYearMdl);
  const { onInvalid, reset, summaryProps } = useValidationSummary({ errors });
  const [loading, setLoading] = useState(false);
  const [fyDocs, setFyDocs] = useState([]);

  useEffect(() => {
    if (updateData && updateData?.id) {
      // The Save button's spinner is the one loader while the record loads.
      setLoading(true);

      API(controller.FiscalYear + action.GetById).getById(
        updateData.id,
        (res) => {
          if (res?.status === 200) {
            var result = res?.result;
            setValues((prev) => ({
              ...prev,
              ...result,
              fyDocs: result?.fyDocs || [],
              startDate: toStoreTime(result.startDate),
              endDate: toStoreTime(result.endDate),
            }));
            res?.result?.fiscalYearDocs?.length > 0 &&
              setFyDocs([...res?.result?.fiscalYearDocs]);
          }

          setLoading(false);
        },
      );
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateData]);

  const handleClose = () => {
    setShowForm(false);
  };

  const validateForm = () => {
    let temp = {};
    temp.title = values.title !== "" ? "" : t('forms:titleRequired');
    temp.startDate = values.startDate !== "" ? "" : t('forms:startDateRequired');
    temp.endDate = values.endDate !== "" ? "" : t('forms:endDateRequired');

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      const onComplete = (res) => {
        setLoading(false);
        if (res && (res.status === 201 || res.status === 200)) {
          reset();
          toast.success(res?.msg);
          setShowForm(false);
          setReloadKey && setReloadKey((prev) => prev + 1);
          reloadSystem && window.location.reload();
        } else toast.error(res?.msg);
      };

      if (!values.id) {
        // Only what a person decides travels: the server works out both instants in the
        // company's own time zone (a first year starts on this day; later years follow on).
        API(controller.FiscalYear + action.Post).post(
          ToFormData({
            companyId: values.companyId,
            startDay: dayjs(values.startDate).format("YYYY-MM-DD"),
            // Honoured only for the company's first year; the server ignores it after that.
            endDay: dayjs(values.endDate).format("YYYY-MM-DD"),
            title: values.title,
            notes: values.notes,
            fyDocs: values.fyDocs,
          }),
          onComplete,
        );
      } else
        API(controller.FiscalYear + action.Put).put(
          ToFormData({
            ...values,
            startDate: new Date(values.startDate).toISOString(),
            endDate: new Date(values.endDate).toISOString(),
          }),
          onComplete,
        );
    } else onInvalid();
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <Paper>
        <Grid container spacing={2.5} className="p-[10px]">
          <Grid
            item
            xs={12}
            className="text-center p-[20px] !pt-[30px] w-[100%]"
          >
            <span className="text-[22px] font-medium uppercase text-gray-500">
              {updateData ? t('forms:updateFiscalYear') : t('forms:openFiscalYear')}
            </span>
            <div className="flex justify-center">
              <div className="border-bottom w-[60px]" />
            </div>
          </Grid>
          {summaryProps.visible && (
            <Grid item xs={12}>
              <FormErrorSummary {...summaryProps} />
            </Grid>
          )}
          <Grid item xs={12}>
            <InputFyDate {...{ values, setValues, errors, handleInpDate }} />
          </Grid>
          <Grid item xs={12}>
            <Input
              label={t('forms:title')}
              name="title"
              value={values.title}
              error={errors.title}
              inputProps={{ maxLength: 150 }}
              onChange={handleInpChg}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              label={t('forms:notes')}
              name="notes"
              value={values.notes}
              error={errors.notes}
              multiline
              rows={3}
              inputProps={{ maxLength: 500 }}
              onChange={handleInpChg}
            />
          </Grid>

          <Grid item xs={12}>
            <InputMultFile
              label={t('forms:uploadFyDoc')}
              name="fyDocs"
              maxSize={5}
              src={fyDocs}
              values={values.fyDocs}
              onChange={handleFile}
              rmv={(file) => {
                setValues((x) => ({
                  ...x,
                  fyDocs: x.fyDocs.filter(
                    (j) => j.lastModified !== file.lastModified,
                  ),
                  fiscalYearDocs: x.fiscalYearDocs.filter(
                    (j) => j.id !== file.id,
                  ),
                }));
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <div className="flex gap-[10px] mt-[10px]">
              <Button
                fullWidth
                size="large"
                variant="text"
                onClick={handleClose}
              >
                {t('cancel')}
              </Button>

              <Button fullWidth size="large" loading={loading} type="submit">
                {t('submit')}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Paper>
    </Form>
  );
};

export default AddEditFiscalYear;
